/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import { colorPalette, sizePalette } from 'utils/styleConstants';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'react-use';
import Icon from './Icon';

const InputWrapper = styled.div<{ justifyContent?: string; fullWidth: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ justifyContent }) => justifyContent ?? 'center'};
  ${(props) => props.fullWidth && 'width: 100%'};
`;

const InputContainer = styled.div<{ fullWidth: boolean; width?: string }>`
  position: relative;
  width: ${({ fullWidth, width }) => (fullWidth ? '100%' : width)};
`;

const ShowPass = styled.div<{ tall?: boolean }>`
  position: absolute;
  right: ${sizePalette.spacing[1.5]};
  top: ${(props) => (props.tall ? sizePalette.spacing[2.5] : sizePalette.spacing[1.5])};
  color: ${({ theme }) => theme.colorPalette.grey.grey5};
`;

export const InputLabel = styled.label<{
  marginRight?: string;
  labelFontSize?: string;
  labelColor?: string;
  labelWidth?: string;
  textAlign?: string;
}>`
  display: inline-block;
  margin-right: ${({ marginRight }) => (marginRight ? marginRight : sizePalette.spacing[4])};
  font-size: ${({ theme, labelFontSize }) => labelFontSize ?? theme.fontSizePalette.small};
  width: ${({ labelWidth }) => labelWidth ?? sizePalette.spacing.pxToRem(80)};
  text-align: ${({ textAlign }) => textAlign ?? 'right'};
  color: ${({ theme, labelColor }) => labelColor ?? theme.colorPalette.black};
`;

const Input = styled.input<StyledInput>`
  font-family: ${({ theme }) => theme.fontFamily};
  margin: ${({ margin }) => (margin ? margin : 'none')};
  color: ${({ theme, fontColor, disabled }) =>
    disabled
      ? theme.colorPalette.isDarkMode
        ? theme.colorPalette.grey.grey5
        : theme.colorPalette.grey.grey7
      : fontColor
      ? fontColor
      : theme.colorPalette.isDarkMode
      ? theme.colorPalette.grey.grey10
      : theme.colorPalette.grey.grey9};
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor
      ? backgroundColor
      : theme.colorPalette.isDarkMode
      ? theme.colorPalette.grey.grey4
      : theme.colorPalette.white};
  font-weight: ${({ theme, fontWeight }) => fontWeight || theme.fontWeightPalette.regular};
  height: ${({ tall, height }) =>
    height ? `${height}` : tall ? sizePalette.spacing.pxToRem(36) : sizePalette.spacing.pxToRem(30)};
  width: ${({ fullWidth, width }) => (fullWidth ? sizePalette.size.full : `${width}`)};
  border: ${({ hasFeedback, theme, noBorder, feedbackColor }) =>
    hasFeedback
      ? `${sizePalette.borderWidth.DEFAULT} solid ${feedbackColor ? feedbackColor : theme.statusColors.error}`
      : noBorder
      ? 'none'
      : `${sizePalette.borderWidth.DEFAULT} solid ${
          theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey5
        }`};
  border-radius: ${({ borderRadius }) => (borderRadius ? `${borderRadius}` : sizePalette.borderRadius.DEFAULT)};
  padding-left: ${sizePalette.spacing[2.5]};
  padding-right: ${({ paddingRight }) => (paddingRight ? sizePalette.spacing.pxToRem(26) : sizePalette.spacing[0])};
  ${({ fontSize, theme }) => `font-size: ${fontSize ? fontSize : theme.fontSizePalette.small}`};

  &::placeholder {
    color: ${({ theme }) => theme.colorPalette.grey.grey7};
  }

  &:focus {
    outline: ${sizePalette.spacing[0]};
    border: ${({ hasFeedback, noBorder, theme, feedbackColor }) =>
      hasFeedback
        ? `${sizePalette.borderWidth.DEFAULT} solid ${feedbackColor}`
        : noBorder
        ? 'none'
        : theme.colorPalette.isDarkMode
        ? `${sizePalette.borderWidth.DEFAULT} solid ${theme.colorPalette.turquoise.accent}`
        : `${sizePalette.borderWidth.DEFAULT} solid ${theme.colorPalette.turquoise.dark}`};
  }

  &:disabled {
    border: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? 'none' : `${sizePalette.borderWidth.DEFAULT} solid ${colorPalette.grey.grey5}`};
    color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.black : theme.colorPalette.grey.grey7)};
    background-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey1 : theme.colorPalette.grey.grey1} !important;
    opacity: ${sizePalette.opacity[70]};
  }

  ::-ms-reveal,
  ::-ms-clear {
    display: none;
  }
`;

export const FeedbackText = styled.p<{
  feedbackColor: string | undefined;
  displayText?: string;
  textPositioning?: string;
}>`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ feedbackColor, theme }) => (feedbackColor ? feedbackColor : theme.statusColors.error)} !important;
  margin: ${sizePalette.spacing.px} ${sizePalette.spacing[0]} ${sizePalette.spacing[0]} ${sizePalette.spacing.px};
  text-align: right;
  position: absolute;
  right: ${sizePalette.spacing[0]};
  white-space: nowrap;
  padding-top: ${sizePalette.spacing[1]};
  ${({ displayText }) => displayText && `display: ${displayText}`};
  ${({ textPositioning }) => textPositioning && `position: ${textPositioning}`};
  word-wrap: break-word;
  white-space: normal;
  box-sizing: border-box;
`;

interface StyledInput {
  hasFeedback: boolean;
  feedbackColor?: string;
  noBorder: boolean;
  fontSize?: string | null;
  paddingRight: boolean;
  tall?: boolean;
  fullWidth?: boolean;
  width?: string;
  backgroundColor?: string;
  height?: string;
  borderRadius?: string;
  fontColor?: string;
  fontWeight?: string;
  margin?: string;
}
export interface TextInputProps {
  type?: string;
  label?: string;
  title?: string;
  tall?: boolean;
  onChange?: Function;
  value?: string | number;
  placeholder?: string;
  hasFeedback?: boolean;
  feedbackMessage?: string;
  feedbackColor?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  focus?: boolean;
  id: string;
  name?: string;
  noBorder?: boolean;
  fontSize?: string;
  fullWidth?: boolean;
  width?: string;
  displayText?: string;
  textPositioning?: string;
  backgroundColor?: string;
  height?: string;
  borderRadius?: string;
  fontColor?: string;
  fontWeight?: string;
  margin?: string;
  labelMarginRight?: string;
  labelFontSize?: string;
  labelWidth?: string;
  labelColor?: string;
  maxLength?: number;
  autoComplete?: string;
  justifyContent?: string;
  className?: string;
  min?: number;
  max?: number;
}

const TextInput = ({
  id,
  type = 'text',
  label,
  tall,
  onBlur,
  onFocus,
  onKeyDown,
  noBorder,
  focus,
  onChange,
  value = '',
  placeholder = '',
  title = '',
  hasFeedback = false,
  feedbackMessage = '',
  feedbackColor,
  fullWidth = false,
  name,
  fontSize,
  disabled = false,
  width,
  displayText,
  textPositioning = 'absolute',
  backgroundColor,
  height,
  borderRadius,
  fontColor,
  fontWeight,
  margin,
  labelMarginRight,
  labelFontSize,
  labelWidth,
  labelColor,
  maxLength,
  autoComplete,
  justifyContent,
  className,
  min,
  max,
}: TextInputProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [inputType, setInputType] = useState<string>(type);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.value);
    onChange && onChange(e);
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useUpdateEffect(() => {
    if (inputRef.current && focus) {
      inputRef.current.focus();
    }
  }, [focus]);

  return (
    <InputWrapper justifyContent={justifyContent} fullWidth={fullWidth} data-walkthrough={id}>
      {label && (
        <InputLabel
          title={label}
          marginRight={labelMarginRight}
          labelFontSize={labelFontSize}
          labelWidth={labelWidth}
          labelColor={labelColor}
        >
          {label}
        </InputLabel>
      )}
      <InputContainer fullWidth={fullWidth} width={width}>
        <Input
          ref={inputRef}
          fullWidth={fullWidth}
          width={width ?? sizePalette.spacing.pxToRem(200)}
          tall={tall}
          fontSize={fontSize ? fontSize : null}
          noBorder={noBorder ? noBorder : false}
          id={id}
          name={name}
          title={title}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          hasFeedback={hasFeedback}
          feedbackColor={feedbackColor}
          disabled={disabled}
          placeholder={placeholder}
          value={currentValue}
          onChange={handleChange}
          type={inputType}
          paddingRight={type === 'password' ? true : false}
          backgroundColor={backgroundColor}
          height={height}
          borderRadius={borderRadius}
          fontColor={fontColor}
          fontWeight={fontWeight}
          margin={margin}
          maxLength={maxLength}
          autoComplete={autoComplete}
          className={className}
          min={min}
          max={max}
        />
        {type === 'password' ? (
          <ShowPass tall={tall}>
            {inputType === 'password' ? (
              <Icon
                style={{
                  color: colorPalette.grey.grey5,
                  cursor: 'pointer',
                }}
                icon="eye"
                onClick={() => setInputType('text')}
              />
            ) : (
              <Icon
                style={{
                  color: colorPalette.grey.grey5,
                  cursor: 'pointer',
                }}
                icon="eye-slash"
                onClick={() => setInputType('password')}
              />
            )}
          </ShowPass>
        ) : null}
        {hasFeedback && feedbackMessage.length > 0 && (
          <FeedbackText displayText={displayText} feedbackColor={feedbackColor} textPositioning={textPositioning}>
            {feedbackMessage}
          </FeedbackText>
        )}
      </InputContainer>
    </InputWrapper>
  );
};

export default TextInput;
